import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Pencil, Loader2, AlertCircle, Plus, X, Lock } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { TagInput } from '@/components/ui/tag-input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { invokeWithFreshAuth } from '@/lib/edge-functions'
import { toast } from 'sonner'

// Edit-in-place editor for a generated SDR BC deck, organized BY SLIDE: the
// left rail lists the deck's slides in render order (template-fixed ones are
// shown locked), the right pane edits the selected slide's data. Saving sends
// a diff patch to the sdr-bc-update edge function — same slug, same URL, no
// research re-run. Region/global totals recompute server-side when row tables
// change; a total the AE types explicitly always wins over the recompute.
// Slide order mirrors supabase/functions/sdr-bc-render/index.ts slideBuilders.

const REGION_ORDER = ['us', 'lat', 'ema', 'apa'] as const
type RegionKey = typeof REGION_ORDER[number]

interface CardsRow { id: string; country: string; iso: string; tpv: string; ar: string; dtpv: string; cost: string }
interface ApmsRow { id: string; country: string; iso: string; apms: string; dtpv: string; cost: string }
interface DevRow { id: string; team: string; m: string }
interface GeoRow { id: string; name: string; share: string }
interface PspRow { id: string; name: string; role: 'integrado' | 'nuevo' }

let _rowSeq = 0
const rowId = () => `er-${++_rowSeq}`

const MONTHS_PER_INTEGRATION = 3  // must match sdr-bc-generate / sdr-bc-update

interface SdrBcContentTarget {
  slug: string
  clientName: string
  defaults: Record<string, unknown>
}

interface SdrBcContentEditorProps {
  open: boolean
  onClose: () => void
  onSaved?: () => void
  target: SdrBcContentTarget | null
}

const asStr = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
const asStrArray = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])

function sameStringArray(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i])
}

// One entry per deck slide, in render order. `pane` = editable pane id; null =
// template-fixed slide (listed so the rail mirrors the real deck, but locked).
interface SlideEntry { pane: string | null; label: string }

export function SdrBcContentEditor({ open, onClose, onSaved, target }: SdrBcContentEditorProps) {
  const defaults = useMemo(() => target?.defaults ?? {}, [target])

  const activeRegions = useMemo(
    () => REGION_ORDER.filter(r => Array.isArray(defaults[`${r}_cards_rows`])),
    [defaults],
  )
  const regionLabel = (r: RegionKey): string => {
    const rr = defaults.regions_rendered
    if (Array.isArray(rr)) {
      const hit = (rr as Array<Record<string, unknown>>).find(x => x?.region === r)
      if (hit && typeof hit.label === 'string') return hit.label
    }
    return r.toUpperCase()
  }

  // Deck slide list in render order (mirrors sdr-bc-render slideBuilders).
  const slides = useMemo<SlideEntry[]>(() => {
    const list: SlideEntry[] = [
      { pane: 'cover', label: 'Cover' },
      { pane: null, label: 'Agenda' },
      { pane: null, label: 'Section — Context' },
      { pane: 'stack', label: 'Payment stack' },
      { pane: Array.isArray(defaults.markets_geo) ? 'geo' : null, label: 'Geography' },
      { pane: 'orch', label: 'Orchestration' },
      { pane: null, label: 'Section — Why Yuno' },
      { pane: null, label: 'Yuno overview' },
    ]
    if (defaults.include_payins_slide === true) list.push({ pane: null, label: 'Pay-ins & Payouts' })
    if (defaults.include_subscriptions_slide === true) list.push({ pane: null, label: 'Subscription Management' })
    if (defaults.include_subscription_demo_slide === true) list.push({ pane: null, label: 'Subscription demo' })
    list.push(
      { pane: null, label: 'Trusted by' },
      { pane: null, label: 'Section — Business case' },
      { pane: 'levers', label: 'Four levers' },
    )
    for (const r of activeRegions) {
      const label = regionLabel(r)
      list.push({ pane: null, label: `${label} — intro` })
      list.push({ pane: `cards:${r}`, label: `${label} — cards` })
      if (Array.isArray(defaults[`${r}_apms_rows`])) list.push({ pane: `apms:${r}`, label: `${label} — APMs` })
      if (Array.isArray(defaults[`${r}_dev_rows`])) list.push({ pane: `dev:${r}`, label: `${label} — dev savings` })
    }
    list.push({ pane: 'closing', label: 'Total & closing' })
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults, activeRegions])

  const [selectedPane, setSelectedPane] = useState('cover')

  // ── Per-slide state ──
  const [clientName, setClientName] = useState('')
  const [sdrName, setSdrName] = useState('')
  const [sdrPosition, setSdrPosition] = useState('')
  const [deckYear, setDeckYear] = useState('')
  const [requireEmailGate, setRequireEmailGate] = useState(false)

  const [acquirers, setAcquirers] = useState<string[]>([])
  const [gateways, setGateways] = useState<string[]>([])
  const [methods, setMethods] = useState<string[]>([])
  const [observations, setObservations] = useState('')

  const [geoRows, setGeoRows] = useState<GeoRow[]>([])

  const [orchTx, setOrchTx] = useState('')
  const [orchAr, setOrchAr] = useState('')
  const [orchCurrentPsps, setOrchCurrentPsps] = useState<string[]>([])
  const [orchCurrentAntifraud, setOrchCurrentAntifraud] = useState('')
  const [orchProposedAntifraud, setOrchProposedAntifraud] = useState('')
  const [orchProposedCascade, setOrchProposedCascade] = useState('')
  const [orchProposedPsps, setOrchProposedPsps] = useState<PspRow[]>([])

  const [cardsByRegion, setCardsByRegion] = useState<Record<string, CardsRow[]>>({})
  const [apmsByRegion, setApmsByRegion] = useState<Record<string, ApmsRow[]>>({})
  const [devByRegion, setDevByRegion] = useState<Record<string, DevRow[]>>({})
  const [devMeta, setDevMeta] = useState<Record<string, { apms: string; psps: string; time: string }>>({})

  // Formatted total tokens, keyed by the exact defaults key (US_TPVUPT, …).
  const [totals, setTotals] = useState<Record<string, string>>({})

  const [vendorName, setVendorName] = useState('')
  const [vendorTitle, setVendorTitle] = useState('')
  const [vendorEmail, setVendorEmail] = useState('')
  const [vendorPhone, setVendorPhone] = useState('')
  const [vendorDemoUrl, setVendorDemoUrl] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Hydrate all panes from the loaded defaults each time the dialog opens.
  useEffect(() => {
    if (!open || !target) return
    const d = target.defaults

    setSelectedPane('cover')
    setClientName(asStr(d.clientName) || target.clientName)
    setSdrName(asStr(d.sdr_name))
    setSdrPosition(asStr(d.sdr_position))
    setDeckYear(asStr(d.date))
    setRequireEmailGate(d.require_email_gate === true)

    setAcquirers(asStrArray(d.acquirers))
    setGateways(asStrArray(d.gateways))
    setMethods(asStrArray(d.methods))
    setObservations(asStrArray(d.stack_observations).join('\n'))

    setGeoRows(
      Array.isArray(d.markets_geo)
        ? (d.markets_geo as Array<Record<string, unknown>>).map(m => ({
            id: rowId(), name: asStr(m?.name), share: asStr(m?.share),
          }))
        : [],
    )

    setOrchTx(asStr(d.orchestration_client_tx_per_month))
    setOrchAr(asStr(d.orchestration_client_ar_pct))
    setOrchCurrentPsps(asStrArray(d.orchestration_current_psps))
    setOrchCurrentAntifraud(asStr(d.orchestration_current_antifraud))
    setOrchProposedAntifraud(asStr(d.orchestration_proposed_antifraud))
    setOrchProposedCascade(asStr(d.orchestration_proposed_antifraud_cascade))
    setOrchProposedPsps(
      Array.isArray(d.orchestration_proposed_psps)
        ? (d.orchestration_proposed_psps as Array<Record<string, unknown>>).map(p => ({
            id: rowId(), name: asStr(p?.name), role: p?.role === 'nuevo' ? 'nuevo' : 'integrado',
          }))
        : [],
    )

    const cards: Record<string, CardsRow[]> = {}
    const apms: Record<string, ApmsRow[]> = {}
    const dev: Record<string, DevRow[]> = {}
    const meta: Record<string, { apms: string; psps: string; time: string }> = {}
    for (const r of REGION_ORDER) {
      const KEY = r.toUpperCase()
      const cRows = d[`${r}_cards_rows`]
      if (Array.isArray(cRows)) {
        cards[r] = (cRows as Array<Record<string, unknown>>).map(row => ({
          id: rowId(), country: asStr(row?.country), iso: asStr(row?.iso),
          tpv: asStr(row?.tpv), ar: asStr(row?.ar), dtpv: asStr(row?.dtpv), cost: asStr(row?.cost),
        }))
      }
      const aRows = d[`${r}_apms_rows`]
      if (Array.isArray(aRows)) {
        apms[r] = (aRows as Array<Record<string, unknown>>).map(row => ({
          id: rowId(), country: asStr(row?.country), iso: asStr(row?.iso),
          apms: asStr(row?.apms), dtpv: asStr(row?.dtpv), cost: asStr(row?.cost),
        }))
      }
      const dRows = d[`${r}_dev_rows`]
      if (Array.isArray(dRows)) {
        dev[r] = (dRows as Array<Record<string, unknown>>).map(row => ({
          id: rowId(), team: asStr(row?.team), m: asStr(row?.m),
        }))
        meta[r] = {
          apms: asStr(d[`${KEY}_DEV_NUM_APMS_INTEG`]),
          psps: asStr(d[`${KEY}_DEV_NUM_PSPS_INTEG`]),
          time: asStr(d[`${KEY}_DEV_TIME`]),
        }
      }
    }
    setCardsByRegion(cards)
    setApmsByRegion(apms)
    setDevByRegion(dev)
    setDevMeta(meta)

    const t: Record<string, string> = {}
    const totalKeys = [
      ...REGION_ORDER.flatMap(r => {
        const KEY = r.toUpperCase()
        return ['TPV_TOT', 'TPVUPT', 'COST_REDTOT', 'REVENUEUP', 'APMUPT', 'COST_APMREDTOT', 'DEV_SAVINGSTOT', 'TOTAL']
          .map(s => `${KEY}_${s}`)
      }),
      'TOTAL_ARUPTOT', 'TOTAL_APMUPT', 'TOTAL_COST_REDTOT', 'TOTAL_DEV_SAVINGS', 'GRAND_TOTAL', 'TPV_BASE',
    ]
    for (const k of totalKeys) {
      if (k in d && d[k] !== null) t[k] = asStr(d[k])
    }
    setTotals(t)

    setVendorName(asStr(d.vendor_name))
    setVendorTitle(asStr(d.vendor_title))
    setVendorEmail(asStr(d.vendor_email))
    setVendorPhone(asStr(d.vendor_phone))
    setVendorDemoUrl(asStr(d.vendor_demo_url))

    setError(null)
  }, [open, target])

  function handleClose() {
    if (submitting) return
    onClose()
  }

  // ── Build the diff patch: only keys whose value actually changed ──
  function buildPatch(): { patch: Record<string, unknown>; error?: string } {
    const d = defaults
    const patch: Record<string, unknown> = {}

    const name = clientName.trim()
    if (name && name !== (asStr(d.clientName) || target?.clientName)) patch.clientName = name

    const sn = sdrName.trim()
    if (sn !== asStr(d.sdr_name)) patch.sdr_name = sn || null
    const sp = sdrPosition.trim()
    if (sp !== asStr(d.sdr_position)) patch.sdr_position = sp || null
    const yr = deckYear.trim()
    if (yr && yr !== asStr(d.date)) patch.date = yr

    if ((d.require_email_gate === true) !== requireEmailGate) patch.require_email_gate = requireEmailGate

    if (!sameStringArray(acquirers, asStrArray(d.acquirers))) patch.acquirers = acquirers
    if (!sameStringArray(gateways, asStrArray(d.gateways))) patch.gateways = gateways
    if (!sameStringArray(methods, asStrArray(d.methods))) patch.methods = methods
    const obsLines = observations.split('\n').map(s => s.trim()).filter(Boolean)
    if (!sameStringArray(obsLines, asStrArray(d.stack_observations))) patch.stack_observations = obsLines

    if (Array.isArray(d.markets_geo)) {
      const cleaned: Array<{ name: string; share: number }> = []
      for (const g of geoRows) {
        if (!g.name.trim()) continue
        const share = parseFloat(g.share)
        if (!Number.isFinite(share) || share < 0 || share > 100) {
          return { patch, error: `Geography: share for "${g.name}" must be a number between 0 and 100` }
        }
        cleaned.push({ name: g.name.trim(), share: Math.round(share) })
      }
      if (JSON.stringify(cleaned) !== JSON.stringify(d.markets_geo)) patch.markets_geo = cleaned
    }

    const tx = orchTx.trim()
    if (tx !== asStr(d.orchestration_client_tx_per_month)) patch.orchestration_client_tx_per_month = tx
    const arStr = orchAr.trim()
    if (arStr !== asStr(d.orchestration_client_ar_pct)) {
      const ar = parseFloat(arStr)
      if (arStr && (!Number.isFinite(ar) || ar < 0 || ar > 100)) {
        return { patch, error: 'Orchestration: approval rate must be a number between 0 and 100' }
      }
      patch.orchestration_client_ar_pct = arStr ? Math.round(ar) : null
    }
    if (!sameStringArray(orchCurrentPsps, asStrArray(d.orchestration_current_psps))) {
      patch.orchestration_current_psps = orchCurrentPsps
    }
    const caf = orchCurrentAntifraud.trim()
    if (caf !== asStr(d.orchestration_current_antifraud)) patch.orchestration_current_antifraud = caf || null
    const paf = orchProposedAntifraud.trim()
    if (paf && paf !== asStr(d.orchestration_proposed_antifraud)) patch.orchestration_proposed_antifraud = paf
    const pafc = orchProposedCascade.trim()
    if (pafc && pafc !== asStr(d.orchestration_proposed_antifraud_cascade)) patch.orchestration_proposed_antifraud_cascade = pafc
    if (Array.isArray(d.orchestration_proposed_psps)) {
      const cleaned = orchProposedPsps
        .filter(p => p.name.trim())
        .map(p => ({ name: p.name.trim(), role: p.role }))
      if (JSON.stringify(cleaned) !== JSON.stringify(d.orchestration_proposed_psps)) {
        patch.orchestration_proposed_psps = cleaned
      }
    }

    for (const r of activeRegions) {
      const KEY = r.toUpperCase()
      const rLabel = regionLabel(r)

      const rows = cardsByRegion[r]
      if (rows) {
        const cleaned = rows
          .filter(row => row.country.trim())
          .map(row => ({
            country: row.country.trim(), iso: row.iso.trim().toUpperCase(),
            tpv: row.tpv.trim(), ar: row.ar.trim(), dtpv: row.dtpv.trim(), cost: row.cost.trim(),
          }))
        if (cleaned.length === 0) return { patch, error: `${rLabel}: cards table needs at least 1 country row` }
        if (JSON.stringify(cleaned) !== JSON.stringify(d[`${r}_cards_rows`])) patch[`${r}_cards_rows`] = cleaned
      }

      const aRows = apmsByRegion[r]
      if (aRows) {
        const cleaned = aRows
          .filter(row => row.country.trim())
          .map(row => ({
            country: row.country.trim(), iso: row.iso.trim().toUpperCase(),
            apms: row.apms.trim(), dtpv: row.dtpv.trim(), cost: row.cost.trim(),
          }))
        if (JSON.stringify(cleaned) !== JSON.stringify(d[`${r}_apms_rows`])) patch[`${r}_apms_rows`] = cleaned
      }

      const dRows = devByRegion[r]
      if (dRows) {
        // Integration counts: numInteg = apms + psps (generator invariant).
        const metaR = devMeta[r] ?? { apms: '', psps: '', time: '' }
        const apmsN = parseInt(metaR.apms, 10)
        const pspsN = parseInt(metaR.psps, 10)
        const apmsChanged = metaR.apms.trim() !== asStr(d[`${KEY}_DEV_NUM_APMS_INTEG`])
        const pspsChanged = metaR.psps.trim() !== asStr(d[`${KEY}_DEV_NUM_PSPS_INTEG`])
        if (apmsChanged || pspsChanged) {
          if (!Number.isFinite(apmsN) || apmsN < 0 || !Number.isFinite(pspsN) || pspsN < 0) {
            return { patch, error: `${rLabel} dev savings: integration counts must be numbers ≥ 0` }
          }
          patch[`${KEY}_DEV_NUM_APMS_INTEG`] = apmsN
          patch[`${KEY}_DEV_NUM_PSPS_INTEG`] = pspsN
          patch[`${KEY}_DEV_NUM_INTEG`] = apmsN + pspsN
        }
        const timeStr = metaR.time.trim()
        if (timeStr && timeStr !== asStr(d[`${KEY}_DEV_TIME`])) patch[`${KEY}_DEV_TIME`] = timeStr

        // Rows: keep them coherent client-side (i = m×3, all = i×numInteg) so
        // the deck stays consistent even if the server-side recompute skips.
        const storedInteg = d[`${KEY}_DEV_NUM_INTEG`]
        const numInteg = apmsChanged || pspsChanged
          ? apmsN + pspsN
          : typeof storedInteg === 'number' ? storedInteg : 0
        const cleaned: Array<{ team: string; m: number; i: number; all: number }> = []
        for (const row of dRows) {
          if (!row.team.trim()) continue
          const m = parseFloat(row.m)
          if (!Number.isFinite(m) || m < 0) {
            return { patch, error: `${rLabel} dev savings: monthly cost for "${row.team}" must be a number ≥ 0` }
          }
          cleaned.push({
            team: row.team.trim(), m,
            i: m * MONTHS_PER_INTEGRATION,
            all: m * MONTHS_PER_INTEGRATION * numInteg,
          })
        }
        if (cleaned.length === 0) return { patch, error: `${rLabel} dev savings: needs at least 1 team row` }
        if (JSON.stringify(cleaned) !== JSON.stringify(d[`${r}_dev_rows`])) patch[`${r}_dev_rows`] = cleaned
      }
    }

    for (const [k, v] of Object.entries(totals)) {
      const trimmed = v.trim()
      // Blank = "leave as stored / let recompute decide", never an explicit wipe.
      if (trimmed && trimmed !== asStr(d[k])) patch[k] = trimmed
    }

    if (vendorName.trim() !== asStr(d.vendor_name)) patch.vendor_name = vendorName.trim()
    if (vendorTitle.trim() !== asStr(d.vendor_title)) patch.vendor_title = vendorTitle.trim()
    if (vendorEmail.trim() !== asStr(d.vendor_email)) patch.vendor_email = vendorEmail.trim()
    if (vendorPhone.trim() !== asStr(d.vendor_phone)) patch.vendor_phone = vendorPhone.trim()
    if (vendorDemoUrl.trim() !== asStr(d.vendor_demo_url)) patch.vendor_demo_url = vendorDemoUrl.trim()

    return { patch }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!target) return
    setError(null)

    const { patch, error: buildErr } = buildPatch()
    if (buildErr) { setError(buildErr); return }
    if (Object.keys(patch).length === 0) {
      toast.info('No changes to save')
      return
    }

    setSubmitting(true)
    try {
      const { data, error: invokeErr } = await invokeWithFreshAuth<{
        ok: boolean
        url: string
        recomputed_keys?: string[]
        warnings?: string[]
      }>('sdr-bc-update', {
        body: { slug: target.slug, patch },
        timeoutMs: 60_000,
      })
      if (invokeErr) {
        const ctx = (invokeErr as { context?: Response }).context
        if (ctx && typeof ctx.json === 'function') {
          try {
            const bodyJson = await ctx.clone().json() as { error?: string } | null
            if (bodyJson?.error) throw new Error(bodyJson.error)
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message && parseErr.message !== 'Unexpected end of JSON input') {
              throw parseErr
            }
          }
        }
        throw invokeErr
      }
      if (!data?.ok) throw new Error('Invalid server response')

      const nChanges = Object.keys(patch).length
      const recomputed = data.recomputed_keys?.length ?? 0
      toast.success(`Deck updated (${nChanges} change${nChanges > 1 ? 's' : ''}${recomputed > 0 ? `, ${recomputed} totals recomputed` : ''})`, {
        description: data.url,
        action: { label: 'Open', onClick: () => window.open(data.url, '_blank', 'noopener') },
      })
      if (data.warnings?.length) {
        toast.warning('Some totals were not recomputed', { description: data.warnings.join(' · ') })
      }
      onSaved?.()
      onClose()
    } catch (err2) {
      setError(err2 instanceof Error ? err2.message : 'Error saving changes')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Small shared renderers ──
  const totalInput = (key: string, label: string) => (
    key in totals ? (
      <div key={key}>
        <Label htmlFor={`ed-t-${key}`} className="text-[10px] text-muted-foreground">{label}</Label>
        <Input
          id={`ed-t-${key}`} value={totals[key] ?? ''}
          onChange={e => setTotals(prev => ({ ...prev, [key]: e.target.value }))}
          disabled={submitting} className="h-8 text-xs text-right font-mono"
        />
      </div>
    ) : null
  )

  const recomputeNote = (
    <p className="text-[11px] text-muted-foreground">
      Totals below auto-recompute from the rows on save — type one only to force a specific value (manual wins).
    </p>
  )

  function renderPane(pane: string) {
    const [kind, region] = pane.split(':') as [string, RegionKey | undefined]
    const KEY = region?.toUpperCase()

    if (kind === 'cover') {
      return (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ed-client" className="text-xs">Client name</Label>
            <Input id="ed-client" value={clientName} onChange={e => setClientName(e.target.value)} disabled={submitting} className="h-9 text-sm" />
          </div>
          <div>
            <Label htmlFor="ed-year" className="text-xs">Year on cover</Label>
            <Input id="ed-year" value={deckYear} onChange={e => setDeckYear(e.target.value)} disabled={submitting} className="h-9 text-sm" placeholder="2026" />
          </div>
          <div>
            <Label htmlFor="ed-sdr-name" className="text-xs">Prepared by — name</Label>
            <Input id="ed-sdr-name" value={sdrName} onChange={e => setSdrName(e.target.value)} disabled={submitting} className="h-9 text-sm" />
          </div>
          <div>
            <Label htmlFor="ed-sdr-pos" className="text-xs">Prepared by — position</Label>
            <Input id="ed-sdr-pos" value={sdrPosition} onChange={e => setSdrPosition(e.target.value)} disabled={submitting} className="h-9 text-sm" />
          </div>
          <label className="col-span-2 mt-1 flex items-start gap-2 rounded-md border border-border p-3 text-sm cursor-pointer">
            <input
              type="checkbox" checked={requireEmailGate}
              onChange={e => setRequireEmailGate(e.target.checked)}
              disabled={submitting} className="h-4 w-4 rounded border-input mt-0.5"
            />
            <div className="flex-1">
              <span>Ask for the viewer&apos;s email before opening the deck</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                ON: modal asks who&apos;s viewing (+ Gmail notification). OFF: opens counted anonymously with location stats.
              </p>
            </div>
          </label>
        </div>
      )
    }

    if (kind === 'stack') {
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Acquirers</Label>
            <TagInput tags={acquirers} onChange={setAcquirers} placeholder="Add acquirer + Enter" />
          </div>
          <div>
            <Label className="text-xs">Gateways / PSPs</Label>
            <TagInput tags={gateways} onChange={setGateways} placeholder="Add gateway + Enter" />
          </div>
          <div>
            <Label className="text-xs">Methods</Label>
            <TagInput tags={methods} onChange={setMethods} placeholder="Add method + Enter" />
          </div>
          <div>
            <Label htmlFor="ed-obs" className="text-xs">Observations (one per line)</Label>
            <Textarea id="ed-obs" value={observations} onChange={e => setObservations(e.target.value)} disabled={submitting} rows={3} className="text-sm" />
          </div>
        </div>
      )
    }

    if (kind === 'geo') {
      return (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Country names must match SimilarWeb spelling — the map resolves pin coordinates by name.
          </p>
          {geoRows.map((g, i) => (
            <div key={g.id} className="flex items-center gap-2">
              <Input
                value={g.name} placeholder="United States"
                onChange={e => setGeoRows(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                disabled={submitting} className="h-9 text-sm flex-1"
              />
              <Input
                value={g.share} placeholder="78" type="number" min="0" max="100"
                onChange={e => setGeoRows(prev => prev.map((x, j) => j === i ? { ...x, share: e.target.value } : x))}
                disabled={submitting} className="h-9 text-sm w-24 shrink-0"
              />
              <span className="text-xs text-muted-foreground">%</span>
              <Button
                type="button" variant="ghost" size="icon"
                onClick={() => setGeoRows(prev => prev.filter((_, j) => j !== i))}
                disabled={submitting || geoRows.length === 1}
                className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                aria-label="Remove market"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button" variant="outline" size="sm"
            onClick={() => setGeoRows(prev => [...prev, { id: rowId(), name: '', share: '' }])}
            disabled={submitting || geoRows.length >= 5}
            className="h-8 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" /> Add market
          </Button>
        </div>
      )
    }

    if (kind === 'orch') {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ed-orch-tx" className="text-xs">Tx / month (est.)</Label>
              <Input id="ed-orch-tx" value={orchTx} onChange={e => setOrchTx(e.target.value)} disabled={submitting} className="h-9 text-sm" placeholder="10M" />
            </div>
            <div>
              <Label htmlFor="ed-orch-ar" className="text-xs">Approval rate % (est.)</Label>
              <Input id="ed-orch-ar" value={orchAr} onChange={e => setOrchAr(e.target.value)} disabled={submitting} className="h-9 text-sm" type="number" min="0" max="100" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Current PSPs</Label>
            <TagInput tags={orchCurrentPsps} onChange={setOrchCurrentPsps} placeholder="Add PSP + Enter" maxTags={4} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="ed-orch-caf" className="text-xs">Current antifraud</Label>
              <Input id="ed-orch-caf" value={orchCurrentAntifraud} onChange={e => setOrchCurrentAntifraud(e.target.value)} disabled={submitting} className="h-9 text-sm" placeholder="(none detected)" />
            </div>
            <div>
              <Label htmlFor="ed-orch-paf" className="text-xs">Proposed antifraud</Label>
              <Input id="ed-orch-paf" value={orchProposedAntifraud} onChange={e => setOrchProposedAntifraud(e.target.value)} disabled={submitting} className="h-9 text-sm" />
            </div>
            <div>
              <Label htmlFor="ed-orch-pafc" className="text-xs">Antifraud cascade</Label>
              <Input id="ed-orch-pafc" value={orchProposedCascade} onChange={e => setOrchProposedCascade(e.target.value)} disabled={submitting} className="h-9 text-sm" />
            </div>
          </div>
          {orchProposedPsps.length > 0 && (
            <div>
              <Label className="text-xs">Proposed PSPs (target diagram)</Label>
              <div className="space-y-2 mt-1">
                {orchProposedPsps.map((p, i) => (
                  <div key={p.id} className="flex items-center gap-2">
                    <Input
                      value={p.name}
                      onChange={e => setOrchProposedPsps(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                      disabled={submitting} className="h-9 text-sm flex-1"
                    />
                    <Select
                      value={p.role}
                      onValueChange={(v) => setOrchProposedPsps(prev => prev.map((x, j) => j === i ? { ...x, role: v === 'nuevo' ? 'nuevo' : 'integrado' } : x))}
                      disabled={submitting}
                    >
                      <SelectTrigger className="h-9 w-32 text-sm shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="integrado">integrado</SelectItem>
                        <SelectItem value="nuevo">nuevo</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button" variant="ghost" size="icon"
                      onClick={() => setOrchProposedPsps(prev => prev.filter((_, j) => j !== i))}
                      disabled={submitting || orchProposedPsps.length === 1}
                      className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                      aria-label="Remove PSP"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setOrchProposedPsps(prev => [...prev, { id: rowId(), name: '', role: 'nuevo' }])}
                  disabled={submitting || orchProposedPsps.length >= 6}
                  className="h-8 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add PSP
                </Button>
              </div>
            </div>
          )}
        </div>
      )
    }

    if (kind === 'levers') {
      return (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            The four global levers + grand total, in $M. These recompute automatically when you edit
            region tables; type here only to force a value. Grand total feeds the cover, this slide
            and the closing slide.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {totalInput('TOTAL_ARUPTOT', 'Δ TPV cards (lever 1)')}
            {totalInput('TOTAL_APMUPT', 'Δ TPV APMs (lever 2)')}
            {totalInput('TOTAL_COST_REDTOT', 'Cost reduction (lever 3)')}
            {totalInput('TOTAL_DEV_SAVINGS', 'Dev savings (lever 4)')}
            {totalInput('GRAND_TOTAL', 'Grand total')}
            {totalInput('TPV_BASE', 'TPV base footnote')}
          </div>
        </div>
      )
    }

    if (kind === 'cards' && region && KEY) {
      const rows = cardsByRegion[region] ?? []
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">Cards table (values in $M)</Label>
            <div className="mt-1 space-y-2">
              <div className="grid grid-cols-[1fr_3.5rem_5.5rem_4rem_5.5rem_5.5rem_2.25rem] gap-1.5 text-[10px] text-muted-foreground px-1">
                <span>Country</span><span>ISO</span><span>TPV</span><span>Δ AR pp</span><span>Δ TPV</span><span>Cost red.</span><span />
              </div>
              {rows.map((row, i) => (
                <div key={row.id} className="grid grid-cols-[1fr_3.5rem_5.5rem_4rem_5.5rem_5.5rem_2.25rem] gap-1.5 items-center">
                  <Input value={row.country} onChange={e => setCardsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, country: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs" />
                  <Input value={row.iso} maxLength={2} onChange={e => setCardsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, iso: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs font-mono uppercase" />
                  <Input value={row.tpv} onChange={e => setCardsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, tpv: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs text-right" />
                  <Input value={row.ar} onChange={e => setCardsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, ar: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs text-right" />
                  <Input value={row.dtpv} onChange={e => setCardsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, dtpv: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs text-right" />
                  <Input value={row.cost} onChange={e => setCardsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, cost: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs text-right" />
                  <Button
                    type="button" variant="ghost" size="icon"
                    onClick={() => setCardsByRegion(prev => ({ ...prev, [region]: prev[region].filter((_, j) => j !== i) }))}
                    disabled={submitting || rows.length <= 1}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    aria-label="Remove row"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => setCardsByRegion(prev => ({ ...prev, [region]: [...(prev[region] ?? []), { id: rowId(), country: '', iso: '', tpv: '', ar: '+4', dtpv: '', cost: '' }] }))}
                disabled={submitting || rows.length >= 5}
                className="h-8 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" /> Add country
              </Button>
            </div>
          </div>
          {recomputeNote}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {totalInput(`${KEY}_TPV_TOT`, 'TPV total')}
            {totalInput(`${KEY}_TPVUPT`, 'Δ TPV total')}
            {totalInput(`${KEY}_COST_REDTOT`, 'Cost red. total')}
            {totalInput(`${KEY}_REVENUEUP`, 'Revenue uplift')}
          </div>
        </div>
      )
    }

    if (kind === 'apms' && region && KEY) {
      const rows = apmsByRegion[region] ?? []
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">APMs table (values in $M)</Label>
            <div className="mt-1 space-y-2">
              <div className="grid grid-cols-[1fr_3.5rem_1.5fr_5.5rem_5.5rem_2.25rem] gap-1.5 text-[10px] text-muted-foreground px-1">
                <span>Country</span><span>ISO</span><span>Recommended APMs</span><span>Δ TPV</span><span>Cost red.</span><span />
              </div>
              {rows.map((row, i) => (
                <div key={row.id} className="grid grid-cols-[1fr_3.5rem_1.5fr_5.5rem_5.5rem_2.25rem] gap-1.5 items-center">
                  <Input value={row.country} onChange={e => setApmsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, country: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs" />
                  <Input value={row.iso} maxLength={2} onChange={e => setApmsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, iso: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs font-mono uppercase" />
                  <Input value={row.apms} onChange={e => setApmsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, apms: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs" />
                  <Input value={row.dtpv} onChange={e => setApmsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, dtpv: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs text-right" />
                  <Input value={row.cost} onChange={e => setApmsByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, cost: e.target.value } : x) }))} disabled={submitting} className="h-8 text-xs text-right" />
                  <Button
                    type="button" variant="ghost" size="icon"
                    onClick={() => setApmsByRegion(prev => ({ ...prev, [region]: prev[region].filter((_, j) => j !== i) }))}
                    disabled={submitting || rows.length <= 1}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    aria-label="Remove row"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          {recomputeNote}
          <div className="grid grid-cols-2 gap-2">
            {totalInput(`${KEY}_APMUPT`, 'Δ TPV total')}
            {totalInput(`${KEY}_COST_APMREDTOT`, 'Cost red. total')}
          </div>
        </div>
      )
    }

    if (kind === 'dev' && region && KEY) {
      const rows = devByRegion[region] ?? []
      const metaR = devMeta[region] ?? { apms: '', psps: '', time: '' }
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs"># APM integrations</Label>
              <Input value={metaR.apms} type="number" min="0" step="1"
                onChange={e => setDevMeta(prev => ({ ...prev, [region]: { ...metaR, apms: e.target.value } }))}
                disabled={submitting} className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs"># PSP integrations</Label>
              <Input value={metaR.psps} type="number" min="0" step="1"
                onChange={e => setDevMeta(prev => ({ ...prev, [region]: { ...metaR, psps: e.target.value } }))}
                disabled={submitting} className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Time label</Label>
              <Input value={metaR.time} placeholder="9 mo"
                onChange={e => setDevMeta(prev => ({ ...prev, [region]: { ...metaR, time: e.target.value } }))}
                disabled={submitting} className="h-9 text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Team costs (USD / month)</Label>
            <p className="text-[11px] text-muted-foreground mb-1">
              Per-integration cost = monthly × {MONTHS_PER_INTEGRATION}; total = per-integration × (# APM + # PSP integrations). Both derive automatically.
            </p>
            <div className="space-y-2">
              {rows.map((row, i) => (
                <div key={row.id} className="flex items-center gap-2">
                  <Input value={row.team}
                    onChange={e => setDevByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, team: e.target.value } : x) }))}
                    disabled={submitting} className="h-8 text-xs flex-1" />
                  <Input value={row.m} type="number" min="0" step="1"
                    onChange={e => setDevByRegion(prev => ({ ...prev, [region]: prev[region].map((x, j) => j === i ? { ...x, m: e.target.value } : x) }))}
                    disabled={submitting} className="h-8 text-xs w-32 text-right shrink-0" />
                </div>
              ))}
            </div>
          </div>
          {recomputeNote}
          <div className="grid grid-cols-2 gap-2">
            {totalInput(`${KEY}_DEV_SAVINGSTOT`, 'Dev savings total ($M)')}
          </div>
        </div>
      )
    }

    if (kind === 'closing') {
      return (
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium">Per-region totals + grand total ($M)</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
              {activeRegions.map(r => totalInput(`${r.toUpperCase()}_TOTAL`, `${regionLabel(r)} total`))}
              {totalInput('GRAND_TOTAL', 'Grand total')}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Auto-recomputed when region tables change; type to override. Grand total feeds the cover, levers and this slide.
            </p>
          </div>
          <div>
            <Label className="text-xs font-medium">Contact</Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              <div>
                <Label htmlFor="ed-v-name" className="text-xs">Name</Label>
                <Input id="ed-v-name" value={vendorName} onChange={e => setVendorName(e.target.value)} disabled={submitting} className="h-9 text-sm" />
              </div>
              <div>
                <Label htmlFor="ed-v-title" className="text-xs">Title</Label>
                <Input id="ed-v-title" value={vendorTitle} onChange={e => setVendorTitle(e.target.value)} disabled={submitting} className="h-9 text-sm" />
              </div>
              <div>
                <Label htmlFor="ed-v-email" className="text-xs">Email</Label>
                <Input id="ed-v-email" type="email" value={vendorEmail} onChange={e => setVendorEmail(e.target.value)} disabled={submitting} className="h-9 text-sm" />
              </div>
              <div>
                <Label htmlFor="ed-v-phone" className="text-xs">Phone</Label>
                <Input id="ed-v-phone" value={vendorPhone} onChange={e => setVendorPhone(e.target.value)} disabled={submitting} className="h-9 text-sm" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="ed-v-demo" className="text-xs">Demo calendar URL</Label>
                <Input id="ed-v-demo" value={vendorDemoUrl} onChange={e => setVendorDemoUrl(e.target.value)} disabled={submitting} className="h-9 text-sm" />
              </div>
            </div>
          </div>
        </div>
      )
    }

    return null
  }

  if (!target) return null

  const selectedIdx = slides.findIndex(s => s.pane === selectedPane)

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent className="max-w-5xl max-h-[90vh] !grid-rows-none !grid-cols-none !flex !flex-col !gap-0 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" aria-hidden="true" />
            Edit deck by slide
            <Badge variant="outline" className="ml-1 text-xs font-mono">{target.slug}</Badge>
          </DialogTitle>
          <DialogDescription>
            Pick a slide on the left, edit its content on the right. Changes save in place — same URL,
            live on next page load. Locked slides are fixed template content.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 mt-2">
          <div className="flex flex-1 min-h-0 gap-3">
            {/* Slide rail — deck order, numbered like the real deck */}
            <nav className="w-56 shrink-0 overflow-y-auto rounded-md border bg-muted/20 p-1.5 space-y-0.5" aria-label="Deck slides">
              {slides.map((s, i) => {
                const num = String(i + 1).padStart(2, '0')
                if (!s.pane) {
                  return (
                    <div key={`${i}-${s.label}`} className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground/60 select-none">
                      <span className="font-mono text-[10px] w-5 shrink-0">{num}</span>
                      <span className="truncate flex-1">{s.label}</span>
                      <Lock className="h-3 w-3 shrink-0" aria-label="Fixed template content" />
                    </div>
                  )
                }
                const active = s.pane === selectedPane
                return (
                  <button
                    key={`${i}-${s.label}`}
                    type="button"
                    onClick={() => setSelectedPane(s.pane!)}
                    className={`flex w-full items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors text-left ${
                      active ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted text-foreground'
                    }`}
                  >
                    <span className="font-mono text-[10px] w-5 shrink-0">{num}</span>
                    <span className="truncate">{s.label}</span>
                  </button>
                )
              })}
            </nav>

            {/* Selected slide pane */}
            <div className="flex-1 min-w-0 overflow-y-auto pr-1">
              <div className="mb-2">
                <span className="text-sm font-medium">
                  {selectedIdx >= 0 ? `Slide ${selectedIdx + 1} — ${slides[selectedIdx].label}` : ''}
                </span>
              </div>
              {renderPane(selectedPane)}

              {error && (
                <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="shrink-0 pt-3">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                'Save all changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
